import { Paperclip, X } from "lucide-react";
import { useId, useRef, useState, type FormEvent } from "react";

import type { PersonRef, TicketDetail } from "../api/types";
import { can, useMe } from "../auth/session";
import { useHotkeys } from "../lib/hotkeys";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Notice, errorMessage } from "../ui/Notice";
import { useAddComment } from "./api";
import { ACCEPT, MAX_FILES, fileProblem } from "./files";
import { formatBytes } from "./labels";
import { PersonPicker } from "./PersonPicker";
import { SavedReplyPicker } from "./savedReplies";

/** Reply to the requester, or (staff) add an internal note with @mentions. R and N jump here. */
/** docked: the reply box at the foot of the conversation box, rather than a card of its own. */
export function Composer({
  ticket,
  onTyping,
  docked = false,
}: {
  ticket: TicketDetail;
  onTyping?: () => void;
  docked?: boolean;
}) {
  const id = useId();
  const me = useMe();
  const canReply = ticket.can.reply;
  const canNote = ticket.can.note;
  const [mode, setMode] = useState<"reply" | "note">(canReply ? "reply" : "note");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [mentions, setMentions] = useState<PersonRef[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const comment = useAddComment(ticket.reference);
  const internal = mode === "note";
  const textarea = useRef<HTMLTextAreaElement>(null);
  function write(next: "reply" | "note") {
    setMode(next);
    textarea.current?.focus();
  }
  useHotkeys(
    {
      r: () => (canReply ? write("reply") : undefined),
      n: () => (canNote ? write("note") : undefined),
    },
    canReply || canNote,
  );

  if (!canReply && !canNote) return null;

  function addFiles(list: FileList | null) {
    if (!list) return;
    const added = Array.from(list);
    const bad = added.map(fileProblem).find(Boolean);
    if (bad) setProblem(bad);
    else if (files.length + added.length > MAX_FILES) setProblem(`Attach at most ${MAX_FILES} files.`);
    else {
      setProblem(null);
      setFiles([...files, ...added]);
    }
    if (fileInput.current) fileInput.current.value = "";
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!body.trim()) {
      setProblem("Write a message first.");
      return;
    }
    comment.mutate(
      { body: body.trim(), isInternal: internal, mentions: internal ? mentions.map((m) => m.id) : [], files },
      {
        onSuccess: () => {
          setBody("");
          setFiles([]);
          setMentions([]);
          setProblem(null);
        },
      },
    );
  }

  const modeSwitch =
    canReply && canNote ? (
      <div className="seg" role="radiogroup" aria-label="Message type">
        <button type="button" role="radio" aria-checked={!internal} className="seg-item" onClick={() => setMode("reply")}>
          {docked ? "Reply" : "Reply to requester"}
        </button>
        <button type="button" role="radio" aria-checked={internal} className="seg-item" onClick={() => setMode("note")}>
          Internal note
        </button>
      </div>
    ) : null;

  return (
    <form
      className={`${docked ? "composer composer-docked" : "card composer"}${internal ? " composer-internal" : ""}`}
      onSubmit={submit}
      noValidate
    >
      {/* Docked, the switch sits in the bottom row beside Send, so the text box comes first. */}
      {docked ? null : modeSwitch}
      <label className="visually-hidden" htmlFor={`${id}-body`}>
        {internal ? "Internal note" : "Reply"}
      </label>
      <textarea
        ref={textarea}
        id={`${id}-body`}
        className="textarea"
        rows={docked ? 2 : 4}
        maxLength={20000}
        placeholder={internal ? "Only staff can see internal notes." : "Write a reply…"}
        value={body}
        onChange={(event) => {
          setBody(event.target.value);
          onTyping?.();
        }}
      />
      {internal ? (
        <div className="composer-mentions">
          {mentions.length ? (
            <span className="badges">
              {mentions.map((person) => (
                <Badge key={person.id} tone="primary">
                  @{person.name}
                  <button
                    type="button"
                    className="chip-remove"
                    aria-label={`Don't mention ${person.name}`}
                    onClick={() => setMentions(mentions.filter((m) => m.id !== person.id))}
                  >
                    <X size={11} aria-hidden="true" />
                  </button>
                </Badge>
              ))}
            </span>
          ) : null}
          <PersonPicker
            label="Mention a sub-admin or admin"
            kind="staff"
            exclude={mentions.map((m) => m.id)}
            onPick={(person) => {
              setMentions([...mentions, person]);
              setBody((text) => (text.includes(`@${person.name}`) ? text : `${text}${text && !text.endsWith(" ") ? " " : ""}@${person.name} `));
            }}
          />
        </div>
      ) : null}
      {files.length ? (
        <ul className="file-list">
          {files.map((file, index) => (
            <li key={`${file.name}-${index}`} className="file-chip">
              <Paperclip size={14} aria-hidden="true" />
              <span className="file-chip-name">{file.name}</span>
              <span className="muted">{formatBytes(file.size)}</span>
              <button
                type="button"
                className="icon-btn icon-btn-sm"
                aria-label={`Remove ${file.name}`}
                onClick={() => setFiles(files.filter((_, i) => i !== index))}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {problem ? <span className="field-error">{problem}</span> : null}
      {comment.isError ? <Notice tone="error">{errorMessage(comment.error)}</Notice> : null}
      <div className="composer-actions">
        <input
          ref={fileInput}
          id={`${id}-files`}
          type="file"
          multiple
          accept={ACCEPT}
          className="visually-hidden"
          onChange={(event) => addFiles(event.target.files)}
        />
        <span className="composer-tools">
          {docked ? modeSwitch : null}
          <label htmlFor={`${id}-files`} className="btn btn-ghost btn-sm">
            <Paperclip size={16} aria-hidden="true" />
            Attach
          </label>
          {can(me, "use_workspace") ? (
            <SavedReplyPicker
              ticket={ticket}
              myName={me.display_name}
              onInsert={(text) => setBody((current) => (current.trim() ? `${current.trimEnd()}\n\n${text}` : text))}
            />
          ) : null}
        </span>
        <Button type="submit" variant="primary" size="sm" loading={comment.isPending}>
          {internal ? "Add note" : "Send reply"}
        </Button>
      </div>
    </form>
  );
}
